/**
 * Shared accessible-dialog wiring for `NewRoomDialog` and `FieldEditor` (both `role="dialog"
 * aria-modal="true"`): Escape closes; initial focus lands on the dialog's first focusable control
 * (or the dialog root itself when it has none); Tab/Shift+Tab cycle within the dialog instead of
 * escaping into the page behind it; focus returns to whatever held it when the dialog opened; and a
 * click on the backdrop (the dialog's own root element, not its content) closes it.
 *
 * The listener is attached in the bubble phase (not capture) so a component nested inside the
 * dialog — e.g. `NewRoomDialog`'s class combobox — can call `event.stopPropagation()` on its own
 * Escape handling (closing just its suggestion list) without also closing the whole dialog.
 *
 * The effect that wires this up runs once per mount: `onClose` is read through a ref so a new
 * `onClose` identity on a re-render (e.g. the dialog's parent re-rendering while it stays open)
 * never re-captures the opener or re-runs the initial focus placement.
 */
import { useEffect, useRef, type MouseEvent, type RefObject } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

function focusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
}

export interface UseDialogResult {
  /** Attach to the dialog's root element (the one carrying `role="dialog"`). */
  dialogRef: RefObject<HTMLDivElement | null>;
  /** Attach as `onClick` on that same root element. */
  onBackdropClick: (event: MouseEvent<HTMLElement>) => void;
}

export function useDialog(onClose: () => void): UseDialogResult {
  const dialogRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);

  // Keep the ref current after every render (rather than assigning it inline during render) so a
  // new `onClose` identity is always visible to the effect below without retriggering it.
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    const dialog = dialogRef.current;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const initial = dialog ? focusableElements(dialog) : [];
    (initial[0] ?? dialog)?.focus();

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab' || !dialog) {
        return;
      }
      const nodes = focusableElements(dialog);
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (!first || !last) {
        event.preventDefault();
        return;
      }
      const active = document.activeElement;
      if (event.shiftKey) {
        if (active === first || !dialog.contains(active)) {
          event.preventDefault();
          last.focus();
        }
      } else if (active === last || !dialog.contains(active)) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      opener?.focus();
    };
  }, []);

  function onBackdropClick(event: MouseEvent<HTMLElement>) {
    if (event.target === event.currentTarget) {
      onCloseRef.current();
    }
  }

  return { dialogRef, onBackdropClick };
}
